import { HiveAccent } from '@/components/brand/hive-accent';
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';

import { api } from '@/lib/api';
import { waTokenAlert } from '@/lib/wa-token-alert';
import { cn } from '@/lib/utils';

/**
 * Workspace-wide notice that the WhatsApp connection is about to expire.
 *
 * Unlike the plan banner, this one IS a lockout warning: when Meta expires the
 * token, sending genuinely stops. So it is worth interrupting for - a tenant who
 * finds out by noticing recovery revenue fell off a cliff has already lost the
 * revenue.
 *
 * Dismissable per state, so "14 days" can be waved away but "3 days" comes back.
 */
export function WaTokenBanner() {
  const { data } = useQuery({
    queryKey: ['whatsapp', 'token-health'],
    queryFn: async () => (await api.get('/api/whatsapp/token-health')).data,
    staleTime: 30 * 60_000,
    retry: false,
  });

  const alert = waTokenAlert(data);
  const key = alert ? `mm_wa_token_${alert.code}` : null;
  const [dismissed, setDismissed] = React.useState(false);
  React.useEffect(() => {
    setDismissed(key ? localStorage.getItem(key) === '1' : false);
  }, [key]);

  if (!alert || dismissed) return null;

  return (
    <div className={cn(
      'flex items-center gap-3 border-b px-4 py-1.5 text-[13px]',
      alert.level === 'danger'
        ? 'card-critical text-red-800 dark:text-red-200'
        : 'card-attention text-amber-900 dark:text-amber-200',
    )}>
      <HiveAccent className="-my-2 w-16" />
      <span className="min-w-0 flex-1">
        <span className="font-semibold">{alert.title}.</span>{' '}
        {alert.detail}{' '}
        <Link to="/integrations/whatsapp" className="font-medium underline underline-offset-2">
          Reconnect WhatsApp
        </Link>
      </span>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => { if (key) localStorage.setItem(key, '1'); setDismissed(true); }}
        className="shrink-0 rounded p-0.5 opacity-60 hover:opacity-100"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}

export default WaTokenBanner;
